import { type FC, type ReactNode, useEffect, useLayoutEffect, useState } from "react"

import {
	type CommunityPostReactionType,
	communityPostReactionLabels,
	communityPostReactionTypes,
} from "@forento/core/models/community"

import { cn } from "../utilities/tailwind"
import CommunityPostReactionIcon from "./CommunityPostReactionIcon"
import Portal from "./Portal"

const margin = 8

type Props = {
	value: CommunityPostReactionType | null
	onSelect(type: CommunityPostReactionType | null): void
	labels?: Record<CommunityPostReactionType, string>
	addLabel?: string
	className?: string
	children: ReactNode
}
const CommunityPostReactionPicker: FC<Props> = ({
	value,
	onSelect,
	labels,
	addLabel = "Add reaction",
	className,
	children,
}) => {
	const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
	const [panel, setPanel] = useState<HTMLDivElement | null>(null)
	const [anchor, setAnchor] = useState<{ centerX: number; top: number; bottom: number } | null>(null)
	const [placement, setPlacement] = useState<{ left: number; top: number; isBelow: boolean } | null>(null)

	const getLabel = (type: CommunityPostReactionType) => labels?.[type] ?? communityPostReactionLabels[type]

	const close = () => {
		setAnchor(null)
		setPlacement(null)
	}

	useLayoutEffect(() => {
		if (panel === null || anchor === null) return

		const rect = panel.getBoundingClientRect()
		const isBelow = anchor.top - rect.height - margin < 0
		setPlacement({
			left: Math.min(
				Math.max(anchor.centerX, rect.width / 2 + margin),
				window.innerWidth - rect.width / 2 - margin,
			),
			top: isBelow ? anchor.bottom + margin : anchor.top - margin,
			isBelow,
		})
	}, [panel, anchor])

	useEffect(() => {
		if (anchor === null) return

		const handleClose = () => {
			setAnchor(null)
			setPlacement(null)
		}
		// Captured and marked handled, so a modal around the picker doesn't also close on the same Escape.
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return
			event.preventDefault()
			handleClose()
		}

		window.addEventListener("resize", handleClose)
		window.addEventListener("scroll", handleClose, true)
		window.addEventListener("keydown", handleKeyDown, true)

		return () => {
			window.removeEventListener("resize", handleClose)
			window.removeEventListener("scroll", handleClose, true)
			window.removeEventListener("keydown", handleKeyDown, true)
		}
	}, [anchor])

	const handleTriggerClick = () => {
		if (anchor !== null || trigger === null) {
			close()
			return
		}
		const rect = trigger.getBoundingClientRect()
		setAnchor({ centerX: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom })
	}

	return (
		<>
			{anchor !== null && (
				<Portal>
					<button
						type="button"
						aria-label="Close reactions"
						onClick={close}
						className="fixed inset-0 cursor-default"
					/>
					<div
						ref={setPanel}
						style={{
							left: placement?.left ?? anchor.centerX,
							top: placement?.top ?? anchor.top - margin,
						}}
						className={cn(
							"fixed flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1 rounded-2xl border border-gray-200 bg-white p-2 text-black shadow-lg",
							!placement?.isBelow && "-translate-y-full",
						)}
					>
						{communityPostReactionTypes.map(type => (
							<button
								key={type}
								type="button"
								aria-pressed={value === type}
								onClick={() => {
									close()
									onSelect(value === type ? null : type)
								}}
								className={cn(
									"flex min-w-16 cursor-pointer flex-col items-center gap-1 rounded-xl px-2 py-1.5",
									value === type ? "bg-gray-100" : "hover:bg-gray-50",
								)}
							>
								<CommunityPostReactionIcon type={type} size={24} />
								<span className="text-xs leading-none whitespace-nowrap text-gray-600">
									{getLabel(type)}
								</span>
							</button>
						))}
					</div>
				</Portal>
			)}
			<button
				ref={setTrigger}
				type="button"
				aria-label={value !== null ? getLabel(value) : addLabel}
				onClick={handleTriggerClick}
				className={cn("flex cursor-pointer items-center", className)}
			>
				{value !== null ? (
					<span className="leading-none font-semibold whitespace-nowrap">{getLabel(value)}</span>
				) : (
					children
				)}
			</button>
		</>
	)
}

export default CommunityPostReactionPicker
